import React from "react";
import { useNavigate } from "react-router-dom";

function calcularEstado(fechaProxima) {
  if (!fechaProxima) {
    return {
      texto: "Sin fecha",
      colorClase: "bg-slate-700 text-slate-200 border-slate-600",
      orden: 4,
    };
  }

  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  const fecha = new Date(`${fechaProxima}T00:00:00`);
  fecha.setHours(0, 0, 0, 0);

  const diferenciaMs = fecha - hoy;
  const diasRestantes = Math.ceil(diferenciaMs / (1000 * 60 * 60 * 24));

  if (diasRestantes < 0) {
    return {
      texto: "Vencido",
      colorClase: "bg-red-950/40 text-red-300 border-red-500/40",
      orden: 1,
    };
  }

  if (diasRestantes <= 30) {
    return {
      texto: "Por vencer",
      colorClase: "bg-yellow-950/40 text-yellow-300 border-yellow-500/40",
      orden: 2,
    };
  }

  return {
    texto: "Vigente",
    colorClase: "bg-emerald-950/40 text-emerald-300 border-emerald-500/40",
    orden: 3,
  };
}

function ResumenCalibracion({ instrumentos = [] }) {
  const navigate = useNavigate();

  const instrumentosConEstado = instrumentos
    .filter((inst) => inst.activo !== false)
    .map((inst) => ({
      ...inst,
      estadoInfo: calcularEstado(inst.fecha_proxima),
    }))
    .sort((a, b) => {
      if (a.estadoInfo.orden !== b.estadoInfo.orden) {
        return a.estadoInfo.orden - b.estadoInfo.orden;
      }

      const fechaA = a.fecha_proxima || "9999-12-31";
      const fechaB = b.fecha_proxima || "9999-12-31";
      return fechaA.localeCompare(fechaB);
    });

  const totalVencidos = instrumentosConEstado.filter(
    (inst) => inst.estadoInfo.texto === "Vencido"
  ).length;

  const totalPorVencer = instrumentosConEstado.filter(
    (inst) => inst.estadoInfo.texto === "Por vencer"
  ).length;

  const totalVigentes = instrumentosConEstado.filter(
    (inst) => inst.estadoInfo.texto === "Vigente"
  ).length;

  const instrumentosMostrados = instrumentosConEstado.slice(0, 5);

  return (
    <div className="mb-6 overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 text-white shadow-sm">
      <div className="border-b border-slate-800 px-5 py-6 sm:px-6">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-400">
          Panel de control
        </p>

        <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
          Estado de calibración
        </h1>

        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-400">
          Supervisa el estado de tus instrumentos, anticipa vencimientos y mantén
          el control de tu programa de calibración.
        </p>
      </div>

      <div className="px-5 py-5 sm:px-6">
        <div className="mb-5 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-red-500/30 bg-red-950/30 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-red-300">
              <span className="h-2 w-2 rounded-full bg-red-400" />
              Vencidos
            </div>

            <div className="mt-3 text-3xl font-bold text-white">
              {totalVencidos}
            </div>

            <p className="mt-1 text-xs text-slate-400">
              Requieren atención
            </p>
          </div>

          <div className="rounded-xl border border-yellow-500/30 bg-yellow-950/30 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-yellow-300">
              <span className="h-2 w-2 rounded-full bg-yellow-400" />
              Por vencer
            </div>

            <div className="mt-3 text-3xl font-bold text-white">
              {totalPorVencer}
            </div>

            <p className="mt-1 text-xs text-slate-400">
              Próximos 30 días
            </p>
          </div>

          <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/30 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-emerald-300">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              Vigentes
            </div>

            <div className="mt-3 text-3xl font-bold text-white">
              {totalVigentes}
            </div>

            <p className="mt-1 text-xs text-slate-400">
              Dentro de vigencia
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-slate-900 border-b border-slate-800">
            <h2 className="text-sm sm:text-base font-medium text-slate-100">
              Instrumentos prioritarios
            </h2>
            <span className="rounded-full border border-slate-700 bg-slate-950 px-3 py-1 text-xs font-medium text-slate-400">
              Hasta 5 instrumentos
            </span>
          </div>

          {instrumentosMostrados.length === 0 ? (
            <div className="px-4 py-6 text-slate-400">
              No hay instrumentos para mostrar.
            </div>
          ) : (
            instrumentosMostrados.map((inst, index) => (
              <div
                key={inst.id || index}
                onClick={() => navigate(`/instrumentos/editar/${inst.id}`)}
                title="Abrir instrumento"
                className={`group cursor-pointer px-4 py-4 transition-colors duration-150 hover:bg-slate-900/70 sm:px-5 ${
                  inst.estadoInfo.texto === "Vencido"
                    ? "bg-red-950/10"
                    : "bg-slate-950"
                } ${
                  index !== instrumentosMostrados.length - 1
                    ? "border-b border-slate-800"
                    : ""
                }`}
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <p className="text-base font-semibold text-white group-hover:text-cyan-300">
                        {inst.nombre || "Sin nombre"}
                      </p>

                      <span className="text-xs font-medium text-slate-500">
                        {inst.identificacion || "Sin identificación"}
                      </span>
                    </div>

                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-400">
                      {inst.ubicacion && (
                        <span>
                          Ubicación:{" "}
                          <span className="text-slate-300">{inst.ubicacion}</span>
                        </span>
                      )}

                      <span>
                        Próxima calibración:{" "}
                        <span className="text-slate-300">
                          {inst.fecha_proxima || "Sin fecha"}
                        </span>
                      </span>
                    </div>
                  </div>

                  <div
                    className={`inline-flex w-fit shrink-0 items-center rounded-full border px-3 py-1.5 text-xs font-semibold ${inst.estadoInfo.colorClase}`}
                  >
                    {inst.estadoInfo.texto}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>        
      </div>
    </div>
  );
}

export default ResumenCalibracion;